import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { db } from "../utils/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { LuPhone, LuMail, LuClock, LuCircleCheck, LuShield, LuArrowRight } from "react-icons/lu";

const CONTACT_CARDS = [
  { Icon: LuPhone, label: "Phone",  value: "(214) 635-6340",              sub: "Call us directly" },
  { Icon: LuMail,  label: "Email",  value: "khojadistributions@gmail.com", sub: "We respond within 24 hrs" },
  { Icon: LuClock, label: "Hours",  value: "Mon – Sat: 10am – 6pm",       sub: "Central Time" },
];

export default function Support() {
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setFormError('');
    try {
      await addDoc(collection(db, 'contactMessages'), { ...form, createdAt: serverTimestamp() });
      setSubmitted(true);
    } catch {
      setFormError('Failed to send message. Please call or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface font-sans">
      <Helmet>
        <title>Support | Khoja Distributions — DFW Wholesale</title>
        <meta name="description" content="Get in touch with Khoja Distributions. Our support team is available to help with orders, wholesale inquiries, and delivery questions in the DFW area." />
        <link rel="canonical" href="https://www.khojadistributions.com/support" />
      </Helmet>

      {/* Hero */}
      <div className="bg-brand-navy pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block text-xs font-bold text-brand-amber uppercase tracking-widest mb-4">Support</span>
          <h1 className="text-4xl font-black text-white tracking-tight mb-4">How can we help?</h1>
          <p className="text-slate-400 text-base max-w-xl mx-auto">
            Our wholesale support team is standing by. Reach out by phone, email, or fill out the form below and we'll get back to you fast.
          </p>
        </div>
      </div>

      {/* Contact cards */}
      <div className="max-w-5xl mx-auto px-6 -mt-10 mb-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {CONTACT_CARDS.map(({ Icon, label, value, sub }) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-brand-navy flex items-center justify-center flex-shrink-0">
                <Icon size={20} className="text-brand-amber" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
                <p className="font-bold text-brand-navy text-sm leading-snug">{value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form + FAQ */}
      <div className="max-w-5xl mx-auto px-6 pb-24 grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

        {/* Contact form */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
          <h2 className="text-xl font-black text-brand-navy mb-1">Send us a message</h2>
          <p className="text-slate-400 text-sm mb-7">Fill out the form and our team will be in touch shortly.</p>

          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                <LuCircleCheck size={32} className="text-green-500" />
              </div>
              <h3 className="font-black text-brand-navy text-lg mb-1">Message sent!</h3>
              <p className="text-slate-400 text-sm">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{formError}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-slate-300 focus:outline-none focus:border-brand-navy transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@company.com" required className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-slate-300 focus:outline-none focus:border-brand-navy transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Phone</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="(000) 000-0000" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-slate-300 focus:outline-none focus:border-brand-navy transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Message *</label>
                <textarea name="message" value={form.message} onChange={handleChange} placeholder="How can we help you?" rows={5} required className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-brand-navy placeholder-slate-300 focus:outline-none focus:border-brand-navy transition-colors resize-none" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-brand-navy hover:bg-brand-amber disabled:opacity-50 transition-colors text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2">
                {loading ? "Sending…" : (<>Send Message <LuArrowRight size={16} /></>)}
              </button>
            </form>
          )}
        </div>

        {/* FAQ / quick help */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-brand-navy rounded-2xl p-7">
            <h3 className="font-black text-white mb-1">Quick Answers</h3>
            <p className="text-slate-400 text-xs mb-5">Common questions from our wholesale partners.</p>
            <div className="space-y-4">
              {[
                { q: "What are your minimum order quantities?", a: "MOQs vary by product category. Check each product page or contact us for bulk pricing." },
                { q: "Do you offer net-30 terms?",              a: "Yes, net-30 payment terms are available for approved wholesale accounts." },
                { q: "How long does shipping take?",            a: "Most orders ship within 1–2 business days. Freight orders may take 3–5 days." },
                { q: "Can I request a product quote?",          a: "Absolutely — use the form or call us directly for a custom wholesale quote." },
              ].map((item, i) => (
                <div key={i} className="border-t border-white/10 pt-4 first:border-t-0 first:pt-0">
                  <p className="text-sm font-bold text-white mb-1">{item.q}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#eef4ff] rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-navy flex items-center justify-center flex-shrink-0">
              <LuShield size={18} className="text-brand-amber" />
            </div>
            <div>
              <p className="font-bold text-brand-navy text-sm mb-1">Verified Wholesale Partner</p>
              <p className="text-xs text-slate-500 leading-relaxed">
                Register for a free B2B account to access wholesale pricing, bulk tiers, and dedicated account support.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
